import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  ButtonDropdown,
  Container,
  Header,
  KeyValuePairs,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationCreateRequest,
  Stage,
} from "../../lusca-search-api/generated-src";
import {
  InvestigationApiError,
  useCreateInvestigation,
  useInvestigationPreview,
} from "../../hooks/useInvestigations";
import { AssignOwnerModal } from "./AssignOwnerModal";
import { builderDraftKey, writeSessionState } from "./drawerSessionState";
import type { BuilderDraft } from "./InvestigationPopulationBuilder";
import { LoadingLine } from "./LoadingLine";
import { DuplicateRuleAlert } from "./PreviewOverlaps";
import { SuggestedSplits } from "./SuggestedSplits";
import {
  CreateOwnership,
  CreatedSummary,
  RULE_FIELDS,
  RowSeed,
  createdSummary,
  defaultInvestigationTitle,
  defaultRule,
  formatSnapshotTimestamp,
  ownershipRequestFields,
  ruleFieldLabel,
  rawSeedValue,
} from "./investigationUtils";

export type { CreatedSummary } from "./investigationUtils";

interface QuickCreateProps {
  stage: Stage;
  seed: RowSeed;
  /** Open the population builder (customise the rule, review overlaps, edit the title). */
  onCustomise: () => void;
  onCreated: (investigationId: string, summary: CreatedSummary) => void;
  /** Open an existing investigation in place (the drawer); falls back to a link in a new tab. */
  onOpenExisting?: (investigationId: string) => void;
}

const FIELD_NOUN: Record<string, string> = {
  eventsourcekey: "queue",
  error_type: "error type",
  error_message: "error message",
  error_category: "error category",
};

/** "queue, error type and error category" */
const joinFields = (fields: string[]): string => {
  const nouns = fields.map(
    (f) => FIELD_NOUN[f] ?? ruleFieldLabel(f).toLowerCase()
  );
  if (nouns.length <= 1) return nouns.join("");
  return `${nouns.slice(0, -1).join(", ")} and ${nouns[nouns.length - 1]}`;
};

/**
 * One-click path for an uninvestigated row: previews the builder's default rule ("items like this
 * one": same queue, error type, templated message and category) as soon as the resolver says
 * nothing owns the item and offers Create right here, claiming by default. Anything that needs a
 * decision (active overlaps, a population over the manifest cap, an empty match, a failed
 * preview) hands over to the builder instead of duplicating its dialogs. Picking a suggested
 * split also hands over, with the split already applied to the builder's draft. Only rendered on
 * stages where the preview is cheap (see AUTO_PREVIEW_STAGES).
 */
export const QuickCreate: React.FC<QuickCreateProps> = ({
  stage,
  seed,
  onCustomise,
  onCreated,
  onOpenExisting,
}) => {
  const rule = useMemo(() => defaultRule(seed), [seed]);
  const ruleSignature = JSON.stringify(rule);
  const previewState = useInvestigationPreview(stage);
  const create = useCreateInvestigation(stage);
  const [createError, setCreateError] = useState<string | null>(null);
  const [duplicateOf, setDuplicateOf] = useState<Investigation | null>(null);
  const [assignVisible, setAssignVisible] = useState(false);

  useEffect(() => {
    if (!rule.length) return;
    setDuplicateOf(null);
    previewState.run(rule).catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the serialised rule on purpose
  }, [ruleSignature, stage]);

  const { preview, pending, error } = previewState;
  const completed = preview?.status === "COMPLETED";
  const count = preview?.count ?? 0;
  const hasActiveOverlap =
    completed &&
    ((preview?.assignedCount ?? 0) > 0 ||
      (preview?.activeOverlaps?.length ?? 0) > 0);
  const canCreate =
    completed &&
    count > 0 &&
    !preview?.truncated &&
    !hasActiveOverlap &&
    !duplicateOf;
  const title = defaultInvestigationTitle(seed);
  // The DLQ item card above already shows the values; only name the fields here.
  const description = rule.length
    ? `Every DLQ item in the latest snapshot with the same ${joinFields(
        rule.map((p) => p.field)
      )} as this one.`
    : undefined;

  const seedValues = useMemo(
    () =>
      Object.fromEntries(
        RULE_FIELDS.map((f) => [f.field, rawSeedValue(seed, f.field)])
      ) as Record<string, string | undefined>,
    [seed]
  );

  /** Seed the builder's draft with the current rule plus this split, then open it. */
  const applySplit = (field: string, value: string) => {
    const draft: BuilderDraft = {
      selected: Array.from(new Set([...rule.map((p) => p.field), field])),
      overrides: { [field]: [value] },
      // Let the builder name the narrowed rule rather than carrying this card's title over.
      editedTitle: null,
    };
    writeSessionState(builderDraftKey(stage, seed), draft);
    onCustomise();
  };

  const submit = async (ownership: CreateOwnership) => {
    if (!preview?.previewId) return;
    setCreateError(null);
    const request = {
      previewId: preview.previewId,
      title,
      ...ownershipRequestFields(ownership),
      mode: "full",
    } as InvestigationCreateRequest;
    try {
      const result = await create.mutateAsync(request);
      if (result.investigation?.id) {
        setAssignVisible(false);
        onCreated(result.investigation.id, createdSummary(result));
      }
    } catch (err) {
      const apiError = err as InvestigationApiError;
      if (apiError.code === "DUPLICATE_RULE" && apiError.current) {
        // Nothing was created: point at the case that already exists instead of showing an error.
        setAssignVisible(false);
        setDuplicateOf(apiError.current);
        return;
      }
      setCreateError(
        apiError.code === "PREVIEW_STALE"
          ? `${apiError.message}. Re-running the preview.`
          : apiError.message
      );
      if (apiError.code === "PREVIEW_STALE")
        previewState.run(rule, { fresh: true }).catch(console.error);
    }
  };

  let customiseLabel = "Build the rule";
  if (canCreate) customiseLabel = "Customise rule…";
  else if (hasActiveOverlap) customiseLabel = "Review overlaps in the builder";

  let body: React.ReactNode;
  if (!rule.length) {
    body = (
      <Alert type="info">
        This row has none of the fields an investigation is usually grouped by;
        build the rule by hand.
      </Alert>
    );
  } else if (duplicateOf) {
    body = (
      <DuplicateRuleAlert
        stage={stage}
        existing={duplicateOf}
        onOpen={onOpenExisting}
      />
    );
  } else if (error || preview?.status === "FAILED") {
    body = (
      <Alert type="warning" header="Preview failed">
        {error?.message || preview?.error}. You can still build the rule by
        hand.
      </Alert>
    );
  } else if (pending || !completed) {
    body = (
      <LoadingLine align="left">Counting items like this one…</LoadingLine>
    );
  } else {
    body = (
      <SpaceBetween size="s">
        <KeyValuePairs
          columns={3}
          items={[
            { label: "Matching items", value: count },
            { label: "Unassigned", value: preview?.unassignedCount ?? 0 },
            {
              label: "Snapshot",
              value: formatSnapshotTimestamp(preview?.snapshotTimestamp),
            },
          ]}
        />
        {count === 0 && (
          <Alert type="info">
            No items share these fields in the latest snapshot (the item may be
            newer than the snapshot or already drained).
          </Alert>
        )}
        {preview?.truncated && (
          <Alert type="warning" header={`Population too large (${count})`}>
            Narrow the rule in the builder before creating.
          </Alert>
        )}
        {hasActiveOverlap && (
          <Alert type="warning" header="Overlaps with active investigations">
            {preview?.assignedCount ?? 0} of these items are already owned.
            Review the overlaps in the builder to contribute, take the remainder
            or create a narrower child.
          </Alert>
        )}
        {createError && <Alert type="error">{createError}</Alert>}
      </SpaceBetween>
    );
  }

  return (
    <Container
      header={
        <Header variant="h3" description={description}>
          Items like this one
        </Header>
      }
      footer={
        completed && !pending ? (
          <SuggestedSplits
            fieldBreakdown={preview?.fieldBreakdown}
            totalCount={preview?.count}
            maxPopulation={preview?.maxPopulation}
            seedValues={seedValues}
            defaultExpanded={!!preview?.truncated}
            onApply={applySplit}
          />
        ) : undefined
      }
    >
      <SpaceBetween size="s">
        {body}
        <SpaceBetween direction="horizontal" size="xs">
          {canCreate && (
            <ButtonDropdown
              variant="primary"
              ariaLabel="More ways to create"
              mainAction={{
                text: `Create and claim (${count.toLocaleString()} items)`,
                loading: create.isLoading,
                onClick: () => {
                  submit("me").catch(console.error);
                },
              }}
              items={[
                {
                  id: "create-assign",
                  text: "Create and assign to…",
                  secondaryText: "Pick a Lusca Search user to own it",
                },
                {
                  id: "create-unowned",
                  text: "Create without owner",
                  secondaryText:
                    "Leave it on the Investigations page for anyone to claim",
                },
              ]}
              onItemClick={({ detail }) => {
                if (detail.id === "create-unowned")
                  submit("nobody").catch(console.error);
                if (detail.id === "create-assign") setAssignVisible(true);
              }}
            />
          )}
          <Button
            variant={canCreate ? "normal" : "primary"}
            onClick={onCustomise}
          >
            {customiseLabel}
          </Button>
        </SpaceBetween>
      </SpaceBetween>
      {assignVisible && (
        <AssignOwnerModal
          stage={stage}
          visible
          title={title}
          confirmLabel={`Create and assign (${count.toLocaleString()} items)`}
          loading={create.isLoading}
          errorText={createError}
          onDismiss={() => setAssignVisible(false)}
          onConfirm={(owner) => {
            submit({ owner }).catch(console.error);
          }}
        />
      )}
    </Container>
  );
};

export default QuickCreate;
